import { AUDIT_KINDS } from "@wallyo/contracts/console";
import { ACTIONS, type ActionArea, type ActionId } from "@wallyo/contracts/actions";
import type { AuditEffect, AuditOutcome, ListAuditLogRequest } from "@wallyo/contracts/console";
import type { MessageId } from "../i18n";
import { screenHref } from "../navigation/screens";

/**
 * The audit log's filters. They live in the URL hash (`#audit?area=payments`)
 * so a filtered view can be shared, and they are sent to the API as-is.
 */
export type AuditFilters = Omit<ListAuditLogRequest, "cursor" | "limit">;

export type FilterName = keyof Required<AuditFilters>;

/** Written in this order, so the same filters are always the same link. */
export const FILTER_NAMES = [
  "subjectUserId",
  "staffMemberId",
  "kind",
  "updaterUuid",
  "userUuid",
  "actionId",
  "area",
  "outcome",
  "effect",
  "from",
  "to",
] as const satisfies readonly FilterName[];

/** Every action ID in the ACTIONS catalog, the choices of the Action filter. */
export const ACTION_IDS: readonly ActionId[] = ACTIONS.map((action) => action.actionId);

/** The areas actions belong to, in catalog order: the same list the server accepts. */
export const AUDIT_AREAS: readonly ActionArea[] = [...new Set(ACTIONS.map((action) => action.area))];

export const OUTCOMES: readonly AuditOutcome[] = ["succeeded", "denied", "requested"];

export const EFFECTS: readonly AuditEffect[] = ["write", "read"];

/**
 * What the screen asks for when nobody said. Reading the log is itself
 * audited, so the reads outnumber the changes many times over and bury them;
 * the log keeps every one, and the screen shows the changes until asked.
 */
export const DEFAULT_EFFECT: AuditEffect = "write";

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const oneOf = <T extends string>(choices: readonly T[], value: string): value is T =>
  (choices as readonly string[]).includes(value);

/**
 * The filters in a hash such as `#audit?outcome=denied&from=2026-09-01`.
 * Values that can't be a filter (an unknown action, a malformed date) are
 * dropped; an id is kept as typed so the screen can say what's wrong with it.
 */
export function filtersFromHash(hash: string): AuditFilters {
  const index = hash.indexOf("?");
  const params = new URLSearchParams(index === -1 ? "" : hash.slice(index + 1));
  const filters: AuditFilters = {};
  const text = (name: FilterName) => params.get(name)?.trim() ?? "";

  const subjectUserId = text("subjectUserId");
  if (subjectUserId) filters.subjectUserId = subjectUserId;
  const staffMemberId = text("staffMemberId");
  if (staffMemberId) filters.staffMemberId = staffMemberId;
  for (const name of ["updaterUuid", "userUuid"] as const) {
    const typed = text(name);
    if (typed !== undefined && /^\d{1,12}$/.test(typed)) filters[name] = Number(typed);
  }
  const kind = text("kind");
  if (oneOf(AUDIT_KINDS, kind)) filters.kind = kind;
  const actionId = text("actionId");
  if (oneOf(ACTION_IDS, actionId)) filters.actionId = actionId;
  const area = text("area");
  if (oneOf(AUDIT_AREAS, area)) filters.area = area;
  const outcome = text("outcome");
  if (oneOf(OUTCOMES, outcome)) filters.outcome = outcome;
  const effect = text("effect");
  if (oneOf(EFFECTS, effect)) filters.effect = effect;
  const from = text("from");
  if (DATE.test(from)) filters.from = from;
  const to = text("to");
  if (DATE.test(to)) filters.to = to;
  return filters;
}

/** The shareable link to the audit log with these filters: `#audit` when there are none. */
export function auditHash(filters: AuditFilters): string {
  const params = new URLSearchParams();
  for (const name of FILTER_NAMES) {
    const value = filters[name];
    // `uuid` is a number; everything else is already text.
    if (value) params.set(name, String(value));
  }
  const query = params.toString();
  return query ? `${screenHref("audit")}?${query}` : screenHref("audit");
}

export function hasFilters(filters: AuditFilters): boolean {
  return FILTER_NAMES.some((name) => Boolean(filters[name]));
}

/**
 * Whether the search names a person. The server refuses one that names
 * nobody, because without somebody to ask about the answer is the whole
 * table; the screen asks for a UUID rather than sending it to be refused.
 */
export function namesSomeone(filters: AuditFilters): boolean {
  return Boolean(filters.updaterUuid) || Boolean(filters.userUuid);
}

/**
 * The filters that narrow results that already came back, not the search
 * itself. The period is one of them as well as a search box: neither end is
 * required, so somebody who knows what they are after can bound it before
 * searching, and anybody else can bound it once they see what came back.
 */
export const NARROWING_NAMES = [
  "actionId",
  "area",
  "outcome",
  "effect",
  "from",
  "to",
] as const satisfies readonly FilterName[];

export function hasNarrowing(filters: AuditFilters): boolean {
  return NARROWING_NAMES.some((name) => Boolean(filters[name]));
}

/** The same search, with the chips let go: the people stay, and only they. */
export function withoutNarrowing(filters: AuditFilters): AuditFilters {
  const next = { ...filters };
  for (const name of NARROWING_NAMES) delete next[name];
  return next;
}

/** `filters` with one filter set, or cleared when `value` is empty. */
export function withFilter(filters: AuditFilters, name: FilterName, value: string): AuditFilters {
  const next: Record<string, string | number> = { ...filters };
  const trimmed = value.trim();
  // The two numeric filters: a UUID is a number, and a box with no digit in
  // it clears it rather than asking the server about nobody (`Number("")`).
  const numeric = name === "updaterUuid" || name === "userUuid";
  const digits = trimmed.replace(/\D/g, "");
  if (numeric && digits) next[name] = Number(digits);
  else if (!numeric && trimmed) next[name] = trimmed;
  else delete next[name];
  return next as AuditFilters;
}

export type FilterProblems = Partial<Record<"subjectUserId" | "staffMemberId" | "to", MessageId>>;

/**
 * What the server would refuse, said next to the field instead of as a failed
 * request. Ids are UUIDs; the period's end can't be before its start.
 */
export function filterProblems(filters: AuditFilters): FilterProblems {
  const problems: FilterProblems = {};
  if (filters.subjectUserId && !UUID.test(filters.subjectUserId)) problems.subjectUserId = "audit.problem.id";
  if (filters.staffMemberId && !UUID.test(filters.staffMemberId)) problems.staffMemberId = "audit.problem.id";
  if (filters.from && filters.to && filters.to < filters.from) problems.to = "audit.problem.period";
  return problems;
}
